import React from 'react';
import { X, ExternalLink, Clock } from 'lucide-react';
import { Badge } from '../ui/Badge';
interface DrillDownModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  metric: string;
}
// Mock data for the drill down with "Days Since Last Activity"
const MOCK_RECORDS = [{
  id: 'LD-1001',
  client: 'John Smith',
  type: 'Refinance',
  amount: '$450,000',
  status: 'Processing',
  date: '2023-10-24',
  daysSinceActivity: 2
}, {
  id: 'LD-1002',
  client: 'Sarah Johnson',
  type: 'Purchase',
  amount: '$325,000',
  status: 'Underwriting',
  date: '2023-10-23',
  daysSinceActivity: 5
}, {
  id: 'LD-1003',
  client: 'Michael Brown',
  type: 'Purchase',
  amount: '$550,000',
  status: 'Docs Sent',
  date: '2023-10-23',
  daysSinceActivity: 12
}, {
  id: 'LD-1004',
  client: 'Emily Davis',
  type: 'Refinance',
  amount: '$280,000',
  status: 'Application',
  date: '2023-10-22',
  daysSinceActivity: 1
}, {
  id: 'LD-1005',
  client: 'David Wilson',
  type: 'Purchase',
  amount: '$415,000',
  status: 'Submitted',
  date: '2023-10-22',
  daysSinceActivity: 9
}];
export function DrillDownModal({
  isOpen,
  onClose,
  title,
  metric
}: DrillDownModalProps) {
  if (!isOpen) return null;
  const getActivityBadge = (days: number) => {
    if (days <= 3) return <Badge variant="success" size="sm" data-id="element-70">
          {days}d ago
        </Badge>;
    if (days <= 7) return <Badge variant="warning" size="sm" data-id="element-71">
          {days}d ago
        </Badge>;
    return <Badge variant="danger" size="sm" data-id="element-72">
        {days}d ago
      </Badge>;
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" data-id="element-73">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-74">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-75"></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-76">
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full" data-id="element-77">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 border-b border-gray-100 flex justify-between items-center" data-id="element-78">
            <div data-id="element-79">
              <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-80">
                {title} Details
              </h3>
              <p className="text-sm text-gray-500 mt-1" data-id="element-81">
                Viewing underlying records for{' '}
                <span className="font-semibold text-gray-700" data-id="element-82">{metric}</span>
              </p>
            </div>
            <button onClick={onClose} className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-83">
              <span className="sr-only" data-id="element-84">Close</span>
              <X className="h-6 w-6" aria-hidden="true" data-id="element-85" />
            </button>
          </div>

          {/* Content */}
          <div className="bg-gray-50 px-4 py-4 sm:p-6" data-id="element-86">
            <div className="flex flex-col" data-id="element-87">
              <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-88">
                <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-89">
                  <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-90">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-91">
                      <thead className="bg-gray-50" data-id="element-92">
                        <tr data-id="element-93">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-94">
                            Loan ID
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-95">
                            Client
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-96">
                            Type
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-97">
                            Amount
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-98">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-99">
                            <div className="flex items-center gap-1" data-id="element-100">
                              <Clock className="h-3 w-3" data-id="element-101" />
                              Last Activity
                            </div>
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-102">
                            Date
                          </th>
                          <th scope="col" className="relative px-6 py-3" data-id="element-103">
                            <span className="sr-only" data-id="element-104">View</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-105">
                        {MOCK_RECORDS.map(record => <tr key={record.id} className="hover:bg-gray-50" data-id="element-106">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-107">
                              {record.id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-108">
                              {record.client}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-109">
                              {record.type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-110">
                              {record.amount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-111">
                              <Badge variant="neutral" size="sm" data-id="element-112">
                                {record.status}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-113">
                              {getActivityBadge(record.daysSinceActivity)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-114">
                              {record.date}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-115">
                              <a href="#" className="text-blue-600 hover:text-blue-900 flex items-center justify-end" data-id="element-116">
                                View <ExternalLink className="ml-1 h-3 w-3" data-id="element-117" />
                              </a>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-white px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-100" data-id="element-118">
            <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={onClose} data-id="element-119">
              Done
            </button>
            <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={onClose} data-id="element-120">
              Export List
            </button>
          </div>
        </div>
      </div>
    </div>;
}